// 発送（design/README.md §4）。発送テーブル＋住所入力（本人入力=フォームURL・見本／代理入力）。
// 第2弾: 住所受領→エントリーの「出荷指示を送る」で倉庫へ0円出荷指示（stoq-worker）→出荷実績の同期で追跡番号と段階5を自動反映。
// 手入力の追跡（エントリーパネルの TrackingForm）も残す。「配達完了」は追跡APIが無いので出さない。
import { useEffect, useMemo, useState } from "react";
import type { GfDealDetail, GfEntry, GfTabProps, Loadable } from "../../../types/gifting";
import { fetchGfDeal } from "../../../api/giftingClient";
import { ageDays, isArchived, STAGE_NAMES } from "../../../lib/giftingRules";
import { GfCard, GfEmpty, GfSeg, GfSkeleton, GfTag, copyText, type GfTone } from "../ui/GfUi";
import { creatorMap, errMsg } from "./kit/helpers";
import { FormPreview } from "./ship/FormPreview";
import { ShipSyncBar } from "./ship/ShipSyncBar";
import { ProxyAddressForm } from "./ship/ProxyAddressForm";

type FormMode = "link" | "proxy";

function shipStatus(e: GfEntry): { label: string; tone: GfTone; order: number } {
  if (e.stage >= 5) return { label: "発送済", tone: "ok", order: 3 };
  if (e.status === "address_received") {
    if (e.ship_order_no) return { label: "出荷指示済", tone: "plain", order: 2 };
    if (e.ship_validation && !e.ship_validation.ok) return { label: "住所要修正", tone: "warn", order: 1 };
    return { label: "出荷待ち", tone: "info", order: 1 };
  }
  return { label: "住所待ち", tone: "caution", order: 0 };
}

function addressLabel(e: GfEntry): string {
  if (e.address_state === "received") return e.address_source === "proxy" ? "代理入力" : "本人入力";
  if (e.address_state === "form_sent") return "フォーム送信済";
  return "—";
}

export function ShipTab(props: GfTabProps) {
  const { data, nav, nowIso, toast, refresh } = props;
  const { board } = data;
  const creators = useMemo(() => creatorMap(board.creators), [board.creators]);
  const campaignsById = useMemo(() => new Map(board.campaigns.map((c) => [c.id, c])), [board.campaigns]);

  const rows = board.entries
    .filter((e) => !isArchived(e) && e.stage >= 4)
    .sort((a, b) => shipStatus(a).order - shipStatus(b).order || ageDays(b, nowIso) - ageDays(a, nowIso));

  // 住所の入力対象（条件合意〜住所回収中）
  const targets = board.entries.filter((e) => !isArchived(e) && (e.stage === 3 || e.stage === 4));
  const idsKey = targets.map((e) => e.id).join(",");

  const [formMode, setFormMode] = useState<FormMode>("link");
  const [targetId, setTargetId] = useState<string | null>(null);
  useEffect(() => {
    const ids = idsKey ? idsKey.split(",") : [];
    setTargetId((cur) => {
      if (nav.state.sel && ids.includes(nav.state.sel)) return nav.state.sel;
      return cur && ids.includes(cur) ? cur : ids[0] ?? null;
    });
  }, [nav.state.sel, idsKey]);

  const target = targets.find((e) => e.id === targetId) ?? null;
  const targetCampaign = target?.campaign_id ? campaignsById.get(target.campaign_id) ?? null : null;
  const targetCreator = target ? creators.get(target.influencer_id) ?? null : null;

  // 本人入力のフォーム URL（PII を含む詳細 API から取得）
  const [detail, setDetail] = useState<Loadable<GfDealDetail>>({ status: "loading" });
  useEffect(() => {
    if (!targetId || formMode !== "link") return;
    let live = true;
    setDetail({ status: "loading" });
    fetchGfDeal(targetId)
      .then((d) => live && setDetail({ status: "ok", data: d }))
      .catch((e: unknown) => live && setDetail({ status: "error", message: errMsg(e) }));
    return () => {
      live = false;
    };
  }, [targetId, formMode, board]);

  const copy = async (url: string) => {
    toast((await copyText(url)) ? "フォームURLをコピーしました" : "コピーできませんでした。URLを手動で選択してください");
  };

  const previewSubtitle = targetCampaign?.name ?? target?.product_name ?? "案件名";

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">発送</h1>
        <ShipSyncBar data={data} refresh={refresh} toast={toast} />
      </div>

      <div className="gf-ship-grid">
        <GfCard flush>
          <div className="gf-table-wrap">
            <table className="gf-table gf-ship-table">
              <thead>
                <tr>
                  <th>クリエイター / 案件</th>
                  <th>住所</th>
                  <th>受注番号</th>
                  <th>配送</th>
                  <th>状態</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const st = shipStatus(e);
                  const camp = e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null;
                  return (
                    <tr
                      key={e.id}
                      className={`is-click${nav.state.sel === e.id ? " is-selected" : ""}`}
                      tabIndex={0}
                      onClick={() => nav.openEntry(e.id, "ship")}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          nav.openEntry(e.id, "ship");
                        }
                      }}
                    >
                      <td>
                        <div className="gf-name-cell-text">
                          <span className="gf-name-strong">{creators.get(e.influencer_id)?.name ?? "（不明）"}</span>
                          <span className="gf-handle gf-ellipsis">{camp?.name ?? e.product_name}</span>
                        </div>
                      </td>
                      <td className="gf-small">{addressLabel(e)}</td>
                      <td>
                        <span className="gf-mono gf-small">{e.ship_order_no ?? "—"}</span>
                      </td>
                      <td className="gf-small">
                        <div className="gf-name-cell-text">
                          <span>{e.ship_carrier ?? "—"}</span>
                          <span className="gf-mono gf-muted">{e.ship_tracking_no ?? ""}</span>
                        </div>
                      </td>
                      <td>
                        <GfTag tone={st.tone}>{st.label}</GfTag>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && <GfEmpty>住所回収以降のエントリーはありません</GfEmpty>}
        </GfCard>

        <div className="gf-ship-right">
          <div className="gf-ship-right-head">
            <span className="gf-h2 gf-h2-sm">住所入力フォーム</span>
            <GfSeg<FormMode>
              ariaLabel="入力方法"
              value={formMode}
              onChange={setFormMode}
              options={[
                { value: "link", label: "本人入力" },
                { value: "proxy", label: "代理入力" },
              ]}
            />
          </div>

          {targets.length > 0 && (
            <label className="gf-field">
              <span className="gf-field-label">対象のエントリー</span>
              <select className="gf-select" value={targetId ?? ""} onChange={(e) => setTargetId(e.target.value)}>
                {targets.map((e) => {
                  const camp = e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null;
                  return (
                    <option key={e.id} value={e.id}>
                      {`${creators.get(e.influencer_id)?.name ?? "（不明）"}｜${camp?.name ?? e.product_name}（${STAGE_NAMES[e.stage]}）`}
                    </option>
                  );
                })}
              </select>
            </label>
          )}

          {formMode === "link" && (
            <>
              <div className="gf-form-card">
                <FormPreview subtitle={previewSubtitle} />
              </div>
              {target ? (
                <>
                  {detail.status === "loading" && <GfSkeleton height={40} />}
                  {detail.status === "error" && <div className="gf-note">フォームURLを取得できませんでした（{detail.message}）</div>}
                  {detail.status === "ok" && detail.data.id === target.id && (
                    <>
                      {detail.data.form_url && detail.data.token_revoked !== 1 ? (
                        <div className="gf-urlbox">
                          <span className="gf-urlbox-text" title={detail.data.form_url}>
                            {detail.data.form_url.replace(/^https?:\/\//, "")}
                          </span>
                          <button type="button" className="gf-copy-btn" onClick={() => void copy(detail.data.form_url as string)}>
                            コピー
                          </button>
                        </div>
                      ) : (
                        <div className="gf-note">このエントリーのフォームURLは無効化されています。エントリーパネルから再発行できます。</div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="gf-note">住所回収中のエントリーを選ぶと、本人に送るフォームURLが表示されます。</div>
              )}
            </>
          )}

          {formMode === "proxy" &&
            (target ? (
              <div className="gf-form-card">
                <ProxyAddressForm
                  entry={target}
                  creator={targetCreator}
                  campaign={targetCampaign}
                  refresh={refresh}
                  toast={toast}
                  onSaved={(ok) => ok && nav.openEntry(target.id, "ship")}
                />
              </div>
            ) : (
              <GfCard>
                <GfEmpty>代理入力できるエントリー（条件合意〜住所回収中）がありません</GfEmpty>
              </GfCard>
            ))}

          <span className="gf-form-fine">
            住所が届くと出荷内容（SKU・郵便番号・電話）を自動でチェックします。エントリー詳細の「出荷指示を送る」で倉庫へ0円出荷指示を送り、倉庫の出荷後（毎日17時台〜）に追跡番号が自動で入ります。
          </span>
        </div>
      </div>
    </>
  );
}
